import { apiService } from '../api/api-client';
import {
  AdjustmentTradeOptionPage,
  AccrualEntry,
  BillingDocument,
  BillingExportJob,
  BillingStatementListItem,
  BillingStatementType,
  CashPoolDocument,
  GenerateStatementResult,
  ManualAdjustment,
  PositionRows,
  StatementExportFormat,
  StatementDocument,
  TradeType,
} from '@/types';
import { PaginatedResponse } from '@/types/common.types';

export interface BillingStatementListParams {
  /** Counterparty id — exact match (LR-551). Prefer over `counterparty_name`,
   *  which is a substring match server-side. */
  counterparty?: number;
  counterparty_name?: string;
  counterparty_account?: string;
  period_start_gte?: string;
  period_start_lte?: string;
  currency?: string;
  status?: string;
  agreement?: string;
  /** Document kind (LR-558 / R11.4). The backend 400s an unknown value. */
  statement_type?: BillingStatementType;
  limit?: number;
  offset?: number;
}

export interface AccrualListParams {
  position_id?: number;
  accrual_date?: string;
  accrual_date_gte?: string;
  accrual_date_lte?: string;
  accrual_kind?: string;
  billing_statement?: number;
  limit?: number;
  offset?: number;
}

export interface BillingPreviewParams {
  // Required by the backend (LR-507): a preview is always scoped to one
  // counterparty's forming statement. `account` / `currency` / `agreement`
  // narrow it further (needed by export to resolve to exactly one statement).
  counterparty: number;
  account?: string;
  currency?: string;
  agreement?: string;
}

export interface GenerateStatementParams {
  /** Counterparty id. */
  counterparty: number;
  /** First day of a completed calendar month, `YYYY-MM-01`. */
  period_start: string;
}

export interface MarkPaidParams {
  /** Settled date `YYYY-MM-DD`; backend defaults to today (UTC) when omitted. */
  paid_date?: string;
  /**
   * Actual amount paid/received as a Decimal STRING (never a float — money);
   * backend defaults to total_accrued when omitted. Same sign convention as
   * total_accrued (positive = due to us).
   */
  paid_amount?: string;
}

export interface ManualAdjustmentListParams {
  /** Counterparty id — the section is scoped to one counterparty (LR-563). */
  counterparty?: number;
  symbol_id?: string;
  trade_type?: TradeType;
  currency?: string;
  /** true = already posted to BO; false = still editable. */
  posted?: boolean;
  limit?: number;
  offset?: number;
}

export interface AdjustmentTradeOptionParams {
  /** Required: unscoped, the endpoint would answer with every book. */
  counterparty: number;
  /** Server-side match on symbol / name / ISIN. */
  search?: string;
  limit?: number;
  /** Pairs to skip; `count` is the total, so more remain while
   *  offset + results.length < count. */
  offset?: number;
}

export interface ManualAdjustmentWriteParams {
  counterparty: number;
  symbol_id: string;
  trade_type: TradeType;
  /** Toggle, not a typed minus: the backend derives the stored sign from this. */
  direction: 'to_us' | 'to_cp';
  /** Positive magnitude as a Decimal STRING (never a float — money). */
  amount: string;
  currency: string;
  /** Value Date for the Back Office posting, `YYYY-MM-DD`. */
  value_date: string;
  comment: string;
  /** Set to reverse (сторно) an already-posted adjustment. */
  reverses?: number | null;
}

export const billingService = {
  async getStatements(
    params?: BillingStatementListParams
  ): Promise<PaginatedResponse<BillingStatementListItem>> {
    return apiService.get<PaginatedResponse<BillingStatementListItem>>(
      '/billing/statements/',
      { params }
    );
  },

  /**
   * The counterparty's document for the statement's period (LR-557): every
   * statement of the same counterparty + month as currency blocks with
   * sections, per-currency summary in the header, no cross-currency total.
   * Addressed by any member statement id — same unit as the export.
   *
   * LR-558: a `cash_pool_interest` statement returns the CashPoolDocument shape
   * instead — currency blocks -> pool sections -> daily lines, every pool of the
   * counterparty in the one document, and the counterparty/period at the top
   * level rather than in a `header`. Narrow the union with `isCashPoolDocument`
   * before reading either shape's fields.
   */
  async getStatementDocument(id: number): Promise<BillingDocument> {
    return apiService.get<BillingDocument>(
      `/billing/statements/${id}/document/`
    );
  },

  /**
   * Manually generate draft statement(s) for one counterparty + a completed
   * calendar month (LR-510). Idempotent server-side: re-running converges on the
   * same statement. Returns the CP+month statement list plus `unbilled_remaining`
   * - true when accruals were left unbilled behind an already-finalized statement.
   */
  async generateStatement(
    params: GenerateStatementParams
  ): Promise<GenerateStatementResult> {
    return apiService.post<GenerateStatementResult>(
      '/billing/statements/generate/',
      params
    );
  },

  /**
   * Mark a statement paid (LR-556): record the settled date + actual amount and
   * move it to `paid`. Allowed from draft/issued; the backend 409s on an
   * already-paid statement. Returns the updated list-shaped row.
   */
  async markPaid(
    id: number,
    params: MarkPaidParams
  ): Promise<BillingStatementListItem> {
    return apiService.post<BillingStatementListItem>(
      `/billing/statements/${id}/mark-paid/`,
      params
    );
  },

  /**
   * Update the optional free-text note (LR-556). Only `notes` is writable
   * through this endpoint (the backend ignores any other field); allowed at any
   * status. Returns the updated list-shaped row.
   */
  async updateNotes(
    id: number,
    notes: string
  ): Promise<BillingStatementListItem> {
    return apiService.patch<BillingStatementListItem>(
      `/billing/statements/${id}/notes/`,
      { notes }
    );
  },

  /**
   * Start a background export of a generated statement (LR-728): POST on the
   * export URL. The server answers with the job - a new one, or the one already
   * building this file for the user. `format` defaults to 'csv'; the endpoint
   * also serves 'pdf' (LR-506). 'xlsx' is not served and 400s.
   *
   * The GET on the same URL still downloads directly, but only what can be built
   * inside the request timeout - a large counterparty's month cannot, which is
   * why the page never uses it.
   */
  async startStatementExport(
    id: number,
    format: StatementExportFormat = 'csv'
  ): Promise<BillingExportJob> {
    return apiService.post<BillingExportJob>(
      `/billing/statements/${id}/export/`,
      undefined,
      { params: { format } }
    );
  },

  /**
   * Month-to-date preview (LR-508 / T5, reshaped by LR-553) - the counterparty's
   * current-month DOCUMENT (GET /billing/preview/): currencies as sections, each
   * with its own total, no total across them. Nothing is written to the DB.
   */
  async getPreview(params: BillingPreviewParams): Promise<StatementDocument> {
    return apiService.get<StatementDocument>('/billing/preview/', { params });
  },

  /**
   * Month-to-date cash pool interest preview (LR-624) — the same endpoint with
   * `statement_type=cash_pool_interest`, answering the CashPoolDocument shape:
   * currency blocks -> pool sections -> daily lines, plus the period's margin
   * call postings; each pool section carries its own `data_through` freshness
   * date (interest posts nightly).
   * One document per counterparty; nothing is written to the DB.
   */
  async getCashPoolPreview(params: {
    counterparty: number;
  }): Promise<CashPoolDocument> {
    return apiService.get<CashPoolDocument>('/billing/preview/', {
      params: { ...params, statement_type: 'cash_pool_interest' },
    });
  },

  /**
   * The daily lines behind ONE position of the MTD document
   * (GET /billing/preview/rows/, LR-571).
   *
   * The document itself carries no rows - a large counterparty's month has
   * hundreds of thousands of them - so a group is expanded on demand. Bounded by
   * construction: a position accrues at most one line per calendar day per leg.
   * The same `counterparty` / `account` / `currency` scoping as `getPreview`, so
   * an expansion can only show lines the collapsed footing counted.
   */
  async getPreviewRows(
    params: BillingPreviewParams & { position: number }
  ): Promise<PositionRows> {
    return apiService.get<PositionRows>('/billing/preview/rows/', { params });
  },

  /**
   * The daily lines behind ONE position of a persisted statement's document
   * (GET /billing/statements/{id}/rows/, LR-571). Twin of `getPreviewRows`.
   */
  async getStatementRows(id: number, position: number): Promise<PositionRows> {
    return apiService.get<PositionRows>(`/billing/statements/${id}/rows/`, {
      params: { position },
    });
  },

  /**
   * Start a background export of the counterparty's MTD document (LR-728):
   * POST /billing/preview/export/ with the same params the GET takes. Exports by
   * params - currency / agreement / account narrow the document rather than
   * selecting between candidates, so it cannot be ambiguous; a month with
   * nothing accrued ends the job as `failed`. Serves 'csv' and 'pdf', each led by
   * a PREVIEW banner since the period is still open.
   */
  async startPreviewExport(
    params: BillingPreviewParams & {
      format?: StatementExportFormat;
      /** `cash_pool_interest` exports the cash pool preview (LR-624). */
      statement_type?: BillingStatementType;
    }
  ): Promise<BillingExportJob> {
    const { format = 'csv', ...rest } = params;
    return apiService.post<BillingExportJob>(
      '/billing/preview/export/',
      undefined,
      { params: { ...rest, format } }
    );
  },

  /** Where a background export is (LR-728). Only its requester can read it. */
  async getExport(id: number): Promise<BillingExportJob> {
    return apiService.get<BillingExportJob>(`/billing/exports/${id}/`);
  },

  /**
   * The finished file of a `done` export, with the server-set filename. Sent
   * gzip-encoded; the browser decodes it before the blob is handed over.
   *
   * Long timeout, not none: the client's default 30 s covers the whole
   * transfer, and a large month's file on a slow link takes longer than that to
   * arrive - the very document the background export exists for. A bound is
   * still needed, or a half-open connection leaves the button busy with no
   * error path and no way out but a reload.
   */
  async downloadExport(
    id: number
  ): Promise<{ blob: Blob; filename: string | null }> {
    return apiService.getFile(`/billing/exports/${id}/download/`, {
      timeout: 10 * 60 * 1000,
    });
  },

  async getAccruals(
    params?: AccrualListParams
  ): Promise<PaginatedResponse<AccrualEntry>> {
    return apiService.get<PaginatedResponse<AccrualEntry>>(
      '/billing/accruals/',
      { params }
    );
  },

  /**
   * Fetch the COMPLETE accrual series for one position — not a single capped
   * page.
   *
   * The accruals endpoint uses DRF LimitOffsetPagination with PAGE_SIZE = 100
   * (backend project/settings/base.py). A position open for >100 business days
   * would have its accrued-income hero silently UNDERCOUNTED if the FE summed
   * only one page. This loops over offset until the series is exhausted
   * (`next === null` and length === count), so the hero (ANLY-01 / HON-03)
   * sums every real AccrualEntry.net_amount row.
   *
   * The model default-orders by `-accrual_date`; the cumulative sum + chart
   * need chronological order, so the combined list is sorted ascending by
   * accrual_date before returning.
   */
  async getAllAccrualsForPosition(positionId: number): Promise<AccrualEntry[]> {
    const PAGE_LIMIT = 1000; // well above PAGE_SIZE=100 to minimise round-trips
    const all: AccrualEntry[] = [];
    let offset = 0;

    // Loop until the server reports no further page AND we have collected the
    // full count. `next` is the authoritative signal for LimitOffsetPagination.
    for (;;) {
      const page = await billingService.getAccruals({
        position_id: positionId,
        limit: PAGE_LIMIT,
        offset,
      });
      all.push(...page.results);

      const exhausted = page.next === null || all.length >= page.count;
      if (exhausted || page.results.length === 0) break;
      offset += page.results.length;
    }

    // Null-safe compare (LR-208 r6 LOW): accrual_date is contractually non-null,
    // but a malformed row must not throw and abort the whole accruals query — the
    // AccrualChart consumer already tolerates a falsy accrual_date.
    return all.sort((a, b) =>
      (a.accrual_date ?? '').localeCompare(b.accrual_date ?? '')
    );
  },

  // --- Manual adjustments (LR-563) -------------------------------------------

  /** List manual adjustments (scoped to a counterparty via `params`). */
  async getAdjustments(
    params?: ManualAdjustmentListParams
  ): Promise<PaginatedResponse<ManualAdjustment>> {
    return apiService.get<PaginatedResponse<ManualAdjustment>>(
      '/billing/adjustments/',
      { params }
    );
  },

  /**
   * The counterparty's book as the (instrument, trade type) pairs an adjustment
   * can be booked against — the instrument dropdown's source.
   *
   * A purpose-built endpoint rather than the blotter list: that one answers a
   * page of ~100 fully-serialized positions (103 fields, ~278 KB) and sweeps
   * TickDB for COB prices and FX per page, none of which a dropdown uses. The
   * collapse to pairs happens in SQL, so `limit`/`offset` page over the PAIRS
   * this renders — a book of 32 000 trades is 6 441 of them.
   *
   * The response is `{count, results}` and nothing else: not the DRF envelope,
   * so not `PaginatedResponse` (which promises `next`/`previous` this endpoint
   * has never sent).
   */
  async getAdjustmentTradeOptions(
    params: AdjustmentTradeOptionParams
  ): Promise<AdjustmentTradeOptionPage> {
    return apiService.get<AdjustmentTradeOptionPage>(
      '/billing/adjustments/trade-options/',
      { params }
    );
  },

  /** Create a manual adjustment (unposted/editable). */
  async createAdjustment(
    params: ManualAdjustmentWriteParams
  ): Promise<ManualAdjustment> {
    return apiService.post<ManualAdjustment>('/billing/adjustments/', params);
  },

  /**
   * Edit an unposted adjustment; the backend 409s if posted. PUT, not PATCH:
   * the edit is a full-form replace and the endpoint routes no partial verb.
   */
  async updateAdjustment(
    id: number,
    params: ManualAdjustmentWriteParams
  ): Promise<ManualAdjustment> {
    return apiService.put<ManualAdjustment>(
      `/billing/adjustments/${id}/`,
      params
    );
  },

  /** Delete an unposted adjustment; the backend 409s if posted. */
  async deleteAdjustment(id: number): Promise<void> {
    return apiService.delete<void>(`/billing/adjustments/${id}/`);
  },

  /**
   * Post an adjustment to the Back Office as one transaction on its Value Date
   * and lock it (LR-563). 409 if already posted; 503 while the BO transaction
   * type is pending BO-8040; 502 if the Back Office rejects it.
   */
  async postAdjustment(id: number): Promise<ManualAdjustment> {
    return apiService.post<ManualAdjustment>(
      `/billing/adjustments/${id}/post/`,
      {}
    );
  },
};
